import {
  Badge,
  Box,
  Card,
  CardBody,
  Divider,
  Heading,
  HStack,
  Progress,
  Text,
  VStack,
} from "@chakra-ui/react";
import { getCityDualDisplayName } from "../../utils/tripDisplay";
import { getTripDisplay } from "../../utils/tripDisplay";
import { countItineraryProgress, getGlobalDayNumber, isFreeDayTrip } from "../../utils/tourTripOptions";

const formatDate = (dateString, locale) =>
  new Date(dateString).toLocaleDateString(locale, {
    timeZone: "Europe/Sarajevo",
    day: "2-digit",
    month: "short",
  });

const TourItinerarySummary = ({ tour, t, language, dateLocale }) => {
  if (!tour?.length) return null;

  const { withActivity, total } = countItineraryProgress(tour);
  const progressValue = total ? (withActivity / total) * 100 : 0;

  return (
    <Card
      borderRadius="2xl"
      border="1px solid"
      borderColor="teal.100"
      shadow="md"
      position={{ lg: "sticky" }}
      top={{ lg: "88px" }}
    >
      <CardBody p={{ base: 4, md: 5 }}>
        <Heading size="sm" color="gray.800" mb={3}>
          {t("tours.builder.itinerarySummaryTitle")}
        </Heading>

        <Box mb={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.600">
              {t("tours.builder.daysWithActivity", {
                planned: withActivity,
                total,
              })}
            </Text>
            <Text fontSize="xs" color="teal.600" fontWeight="semibold">
              {Math.round(progressValue)}%
            </Text>
          </HStack>
          <Progress
            value={progressValue}
            size="sm"
            colorScheme="teal"
            borderRadius="full"
          />
        </Box>

        <VStack align="stretch" spacing={4} maxH={{ lg: "420px" }} overflowY="auto">
          {tour.map((group, groupIndex) => (
            <Box key={groupIndex}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="bold" color="gray.800">
                  {getCityDualDisplayName(group.destination, t, language)}
                </Text>
                <Badge colorScheme="teal" variant="subtle" borderRadius="full">
                  {group.trips.length}{" "}
                  {group.trips.length === 1
                    ? t("tours.builder.day")
                    : t("tours.builder.days")}
                </Badge>
              </HStack>
              <VStack align="stretch" spacing={2}>
                {group.trips.map((trip, tripIndex) => {
                  const display = getTripDisplay(trip, t, language);
                  const freeDay = isFreeDayTrip(trip);

                  return (
                    <Box
                      key={`${groupIndex}-${tripIndex}`}
                      p={2}
                      borderRadius="lg"
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.100"
                    >
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {t("tours.builder.dayLabel", {
                          number: getGlobalDayNumber(tour, groupIndex, tripIndex),
                        })}
                        {trip.date ? ` · ${formatDate(trip.date, dateLocale)}` : ""}
                      </Text>
                      <Text fontSize="sm" color="gray.700" noOfLines={2}>
                        {freeDay
                          ? t("tours.tripCard.freeActivityDay")
                          : display.description}
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
              {groupIndex < tour.length - 1 && <Divider mt={4} />}
            </Box>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default TourItinerarySummary;
